/*
 * Contact — cloned to match the dacharya.com contact layout
 * (hero, form + map, teal info band). Uses the site's default
 * teal/cream/orange palette + Georgia serif headings.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { useToast } from '../hooks/use-toast';
import { Mail, Phone, MapPin, Loader2, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TEAL = '#0d6d64';
const TEAL_DARK = '#0a544d';
const ORANGE = '#e67e22';
const NAVY = '#0f1e3d';

// Office address used across the page
const OFFICE_ADDRESS_LINE_1 = '103 1st Floor Kshitij building, Veera Desai Road,';
const OFFICE_ADDRESS_LINE_2 = 'Dattaguru Nagar Azad Nagar, Andheri West';
const OFFICE_ADDRESS_LINE_3 = 'Mumbai, Maharashtra - 400053';
const OFFICE_ADDRESS_QUERY = '103 1st Floor Kshitij building, Veera Desai Road, Andheri West, Mumbai, Maharashtra 400053';
const GOOGLE_MAPS_LINK = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(OFFICE_ADDRESS_QUERY)}`;
const GOOGLE_MAPS_EMBED = `https://www.google.com/maps?q=${encodeURIComponent(OFFICE_ADDRESS_QUERY)}&output=embed`;

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    parent_type: '',
    parent_name: '',
    whatsapp: '',
    child_age: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.parent_name.trim() || !form.whatsapp.trim()) {
      toast({ title: 'Please share your name and WhatsApp number.', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/inquiries`, {
        parent_name: form.parent_name.trim(),
        phone: form.whatsapp.trim(),
        child_age: form.child_age ? parseInt(form.child_age, 10) : 0,
        message: `Source: Contact page. Parent role: ${form.parent_type || 'Not specified'}.`,
      });
      setSubmitted(true);
      toast({ title: "Thanks! We'll reach out on WhatsApp shortly." });
      setForm({ parent_type: '', parent_name: '', whatsapp: '', child_age: '' });
    } catch (err) {
      toast({
        title: 'Could not send inquiry',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <Navigation />

      {/* ===================== HERO ===================== */}
      <section className="pt-16 sm:pt-24 pb-10 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.15]"
            style={{ fontFamily: 'Georgia, "Playfair Display", serif', color: NAVY }}
            data-testid="contact-hero-title"
          >
            Let&rsquo;s{' '}
            <span style={{ color: ORANGE, fontStyle: 'italic' }}>Build,</span>
            <br />
            Better Learning Together
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
            We believe better communication creates better learning outcomes. Connect with our team and discover how SANJAYA can support students, teachers, and parents together.
          </p>
        </div>
      </section>

      {/* ================= FORM + MAP ================= */}
      <section className="pb-16 px-4">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          {/* Left — Inquiry form */}
          <div data-testid="contact-form-block">
            <h2
              className="text-2xl sm:text-3xl font-bold mb-6"
              style={{ fontFamily: 'Georgia, serif', color: NAVY }}
            >
              Need Help? We&rsquo;re Here for You
            </h2>

            {submitted ? (
              <div className="rounded-3xl border border-slate-200 bg-white shadow-sm p-8 text-center" data-testid="contact-submitted">
                <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                  <svg className="w-7 h-7 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                </div>
                <h3 className="text-xl font-bold" style={{ fontFamily: 'Georgia, serif', color: NAVY }}>Inquiry Received</h3>
                <p className="mt-2 text-slate-600 text-sm">Our team will reach out on WhatsApp within 24 hours.</p>
                <Button variant="outline" onClick={() => setSubmitted(false)} className="mt-5" data-testid="contact-send-another">
                  Send another
                </Button>
              </div>
            ) : (
              <form onSubmit={submit} className="rounded-3xl border border-slate-200 bg-white shadow-sm p-6 sm:p-8" data-testid="contact-inquiry-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-[10px] tracking-[0.2em] font-semibold text-slate-500 uppercase">Parent</label>
                    <select
                      value={form.parent_type}
                      onChange={(e) => setForm({ ...form, parent_type: e.target.value })}
                      className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-white text-slate-500 focus:outline-none focus:border-slate-400"
                      data-testid="contact-parent-type"
                    >
                      <option value="">Select</option>
                      <option>Mother</option>
                      <option>Father</option>
                      <option>Guardian</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] tracking-[0.2em] font-semibold text-slate-500 uppercase">Parent Name</label>
                    <Input
                      placeholder="Name"
                      value={form.parent_name}
                      onChange={(e) => setForm({ ...form, parent_name: e.target.value })}
                      className="mt-1.5 rounded-lg"
                      data-testid="contact-parent-name"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] tracking-[0.2em] font-semibold text-slate-500 uppercase">WhatsApp Number</label>
                    <Input
                      placeholder="+91 XXXX XXXX XX"
                      value={form.whatsapp}
                      onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
                      className="mt-1.5 rounded-lg"
                      data-testid="contact-whatsapp"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] tracking-[0.2em] font-semibold text-slate-500 uppercase">Child&rsquo;s Age</label>
                    <select
                      value={form.child_age}
                      onChange={(e) => setForm({ ...form, child_age: e.target.value })}
                      className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-white text-slate-500 focus:outline-none focus:border-slate-400"
                      data-testid="contact-child-age"
                    >
                      <option value="">Select</option>
                      {[5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map((a) => (
                        <option key={a} value={a}>{a}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="mt-6 w-full text-white rounded-full tracking-[0.15em] text-sm font-bold h-12"
                  style={{ background: NAVY }}
                  data-testid="contact-submit-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  SUBMIT INQUIRY
                </Button>
              </form>
            )}
          </div>

          {/* Right — Map */}
          <div className="relative rounded-3xl overflow-hidden shadow-md border border-slate-200 h-[380px] sm:h-[440px]" data-testid="contact-map-block">
            <iframe
              title="Sanjaya office location"
              src={GOOGLE_MAPS_EMBED}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="contact-map-iframe"
            />
            <a
              href={GOOGLE_MAPS_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-white text-slate-700 rounded-md px-3 py-1.5 text-sm font-medium shadow-md hover:shadow-lg transition-shadow"
              data-testid="contact-open-in-maps"
            >
              Open in Maps <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </section>

      {/* ================= TEAL INFO BAND ================= */}
      <section className="px-4 pb-16 sm:pb-20">
        <div
          className="relative max-w-6xl mx-auto rounded-3xl overflow-hidden px-6 sm:px-12 py-12 sm:py-14"
          style={{ background: TEAL_DARK }}
          data-testid="contact-info-band"
        >
          {/* Decorative shapes (subtle) */}
          <span aria-hidden="true" className="absolute top-6 left-6 text-white/20 text-2xl">✦</span>
          <span aria-hidden="true" className="absolute top-10 right-14 text-white/20 text-2xl">○</span>
          <span aria-hidden="true" className="absolute bottom-8 left-20 text-white/15 text-3xl">+</span>
          <span aria-hidden="true" className="absolute bottom-6 right-8 text-white/20 text-2xl">✦</span>

          <div className="relative grid grid-cols-1 md:grid-cols-3 gap-10 text-white text-center md:text-left">
            {/* Email */}
            <div className="flex flex-col md:flex-row items-center md:items-start gap-4" data-testid="contact-info-email">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 shadow-md"
                style={{ background: '#f5a623' }}
              >
                <Mail className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Georgia, serif' }}>Email Address</h3>
                <a
                  href="mailto:contact@sanjaya.com"
                  className="mt-1 inline-block text-sm text-white/90 hover:text-white"
                  data-testid="contact-info-email-link"
                >
                  contact@sanjaya.com
                </a>
              </div>
            </div>

            {/* Phone */}
            <div className="flex flex-col md:flex-row items-center md:items-start gap-4" data-testid="contact-info-phone">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 shadow-md"
                style={{ background: '#f5a623' }}
              >
                <Phone className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Georgia, serif' }}>Phone Number</h3>
                <a
                  href="tel:+918291188188"
                  className="mt-1 inline-block text-sm text-white/90 hover:text-white"
                  data-testid="contact-info-phone-link"
                >
                  +91 8291188188
                </a>
              </div>
            </div>

            {/* Office Address */}
            <div className="flex flex-col md:flex-row items-center md:items-start gap-4" data-testid="contact-info-office">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 shadow-md"
                style={{ background: '#f5a623' }}
              >
                <MapPin className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Georgia, serif' }}>Office Address</h3>
                <p className="mt-1 text-sm text-white/90 leading-relaxed">
                  {OFFICE_ADDRESS_LINE_1}<br />
                  {OFFICE_ADDRESS_LINE_2}<br />
                  {OFFICE_ADDRESS_LINE_3}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
